import React from "react";

import { unknownAuthorName } from "../../helpers";
import { strapiMediaUrl } from "../../utils/strapi";
import { EntityCard } from "../EntityCard";
import { EntityCardList } from "../EntityCardList";
import { ShapeStatusTags } from "../ShapeStatusTags";

type PublishStatus = "draft" | "published" | "modified";

type MyShapeLocal = {
  key: string;
  name: string;
  updatedAt: string | null;
  image: string | null;
  isLocal: true;
};

type MyShapeVersion = {
  documentId: string;
  version: number;
  reviewStatus: "review_requested" | null;
  status: PublishStatus;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
};

type MyShape = {
  documentId: string;
  name: string;
  slug: string;
  reviewStatus: "review_requested" | null;
  status: PublishStatus;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  versions: MyShapeVersion[];
  // Strapi media object, from the shape's draft row (so on a published
  // shape this is the preview that goes live at the next publish).
  thumbnail: {
    url: string;
    formats?: {
      small?: { url: string };
      thumbnail?: { url: string };
    } | null;
  } | null;
};

export function ShapeList({
  allStrapiShape,
  searchQuery,
  className,
}: {
  allStrapiShape: {
    edges: readonly { node: Queries.ShapeListFragment }[];
  };
  searchQuery?: string;
  className?: string;
}) {
  const shapes = allStrapiShape.edges.map(({ node }) => node);
  return (
    <GenericShapeList
      className={className}
      shapes={shapes}
      searchQuery={searchQuery}
      renderShapeCard={(shape) => {
        let bottomText;
        if (shape.shapeType === "primitive") {
          bottomText = `Primitive shape`;
        } else if (shape.shapeType === "composite") {
          bottomText = `Composite shape`;
        } else {
          bottomText = `by ${shape.author?.name ?? shape.user?.username ?? unknownAuthorName}`;
        }

        return (
          <EntityCard
            key={shape.slug}
            linkTo={"/shapes/" + shape.slug}
            title={shape.name}
            subline={`${shape.countParts} parts, ${shape.countForks} forks`}
            image={shape.thumbnail?.localFile}
            bottom={bottomText}
          />
        );
      }}
    />
  );
}

export function MyShapeList({
  shapes,
  searchQuery,
  className,
}: {
  shapes: (MyShape | MyShapeLocal)[];
  searchQuery?: string;
  className?: string;
}) {
  return (
    <GenericShapeList
      className={className}
      shapes={shapes}
      searchQuery={searchQuery}
      renderShapeCard={(shape) => {
        if (isMyShapeLocal(shape)) {
          const subline = `Updated on ${new Date(
            shape.updatedAt ?? "",
          ).toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}`;
          return (
            <EntityCard
              key={`local-${shape.key}`}
              linkTo={
                "/shapes/my-shapes/create?local=" +
                encodeURIComponent(shape.key)
              }
              title={shape.name}
              subline={subline}
              image={shape.image}
              bottom={"Local storage (Browser)"}
            />
          );
        } else {
          const allUpdatedDates = [
            shape.updatedAt,
            ...shape.versions.map((v) => v.updatedAt),
          ];
          const mostRecentUpdate = allUpdatedDates.reduce((latest, current) => {
            return new Date(current) > new Date(latest) ? current : latest;
          }, shape.updatedAt);

          let subline;
          if (
            (shape.status === "published" || shape.status === "modified") &&
            shape.publishedAt
          ) {
            subline = `Published on ${new Date(
              shape.publishedAt,
            ).toLocaleString(undefined, {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}`;
          } else {
            subline = `Updated on ${new Date(mostRecentUpdate).toLocaleString(
              undefined,
              {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              },
            )}`;
          }

          return (
            <EntityCard
              key={`remote-${shape.documentId}`}
              linkTo={
                "/shapes/my-shapes/create?remote=" +
                encodeURIComponent(shape.documentId)
              }
              title={shape.name}
              subline={subline}
              image={remoteThumbnailUrl(shape.thumbnail)}
              bottom={<MyShapeStatusTags shape={shape} />}
            />
          );
        }
      }}
    />
  );
}

function GenericShapeList<T extends { name: string | null }>({
  shapes,
  searchQuery,
  renderShapeCard,
  className,
}: {
  shapes: T[];
  searchQuery?: string;
  renderShapeCard: (shape: T) => React.ReactNode;
  className?: string;
}) {
  const filteredShapes = shapes.filter((item) => {
    const isMatchesSearchQuery =
      !searchQuery ||
      (item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
    return isMatchesSearchQuery;
  });

  const showNoResults = filteredShapes.length === 0 && searchQuery;

  return (
    <>
      {filteredShapes.length > 0 && (
        <EntityCardList className={className}>
          {filteredShapes.map((shape) => renderShapeCard(shape))}
        </EntityCardList>
      )}
      {showNoResults && (
        <div className={className}>
          No shapes found for <strong>&quot;{searchQuery}&quot;</strong>
        </div>
      )}
    </>
  );
}

function MyShapeStatusTags({ shape }: { shape: MyShape }) {
  const displayedVersions = shape.versions.filter(
    (version) =>
      version.status !== shape.status &&
      version.status !== "published" &&
      (version.reviewStatus === null ||
        version.reviewStatus !== shape.reviewStatus),
  );
  return <ShapeStatusTags shape={shape} versions={displayedVersions} />;
}

function isMyShapeLocal(shape: MyShape | MyShapeLocal): shape is MyShapeLocal {
  return (shape as MyShapeLocal).isLocal === true;
}

// The cards render small, so the 500px `small` format is plenty (covers
// 2x retina) and lighter than the original upload.
function remoteThumbnailUrl(thumbnail: MyShape["thumbnail"]): string | null {
  if (!thumbnail) {
    return null;
  }
  return strapiMediaUrl(thumbnail.formats?.small?.url ?? thumbnail.url);
}
